/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';
import Syphon from 'backbone.syphon';
import Ladda from 'ladda';

import { formatDisplayDate } from '@/util/MomentUtils';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import makeFormReadOnly from '@/util/makeFormReadOnly';
import showErrorMessage from '@/util/showErrorMessage';
import { validateForm } from '@/util/ParsleyUtils';
import OrderTypes from '@/enums/OrderTypes';

import CalculateDatesForOrder from '../../services/CalculateDatesForOrder';
import UpdateOrderInformation from '../../services/UpdateOrderInformation';
import ValidateOrderModule from '../../services/ValidateOrderModule';

import tmplOrderInformationView from './OrderInformationView.hbs';

const OrderInformationView = View.extend({
  template: tmplOrderInformationView,

  className: 'order-information-form',

  ui: {
    popDate: 'input[name="popDate"]',
    orderForm: '#orderGeneralForm',
    trackerNumber: 'input[name="trackerNumber"]',
    piinSpiin: 'input[name="replacementPIINSPIIN"]',
    errPopDate: '.err-pop-date',
    requiredCustomerContactDate: 'input[name="requiredCustomerContactDate"]',
    actualCustomerContactDate: 'input[name="actualCustomerContactDate"]',
    epcSubmissionDate: 'input[name="epcSubmissionDate"]',
    orderPackageSubmittedOn: 'input[name="orderPackageSubmittedOn"]',
    btnDateCalc: '.btn-calculate-dates',
    dateInputs: 'input.datepicker'
  },

  events: {
    'click @ui.btnDateCalc': 'onClickDateCalc',
    'change @ui.popDate': 'onChangePopDate'
  },

  templateContext() {
    const relocationForm = this.model.get('order').orderFormTypeId === OrderTypes.RELOCATION;
    const popUpdatedBy = this.model.get('orderInfo').popUpdatedBy;
    const popUpdatedOn = formatDisplayDate(this.model.get('orderInfo').popUpdatedOn);

    //POP Start Date rules:
    const popFirstOfMonth = !(
      this.model.get('order').orderFormTypeId === OrderTypes.WIDE ||
      this.model.get('order').orderFormTypeId === OrderTypes.RELOCATION
    );
    return {
      relocationForm,
      popUpdatedBy,
      popUpdatedOn,
      popFirstOfMonth
    };
  },

  onRender() {
    const order = this.model.get('order');
    this.ui.popDate.datepicker({ format: 'mm/dd/yyyy' });
    if (order.popDate) {
      this.ui.popDate.datepicker('setDate', formatDisplayDate(order.popDate));
    }

    this.ui.requiredCustomerContactDate.datepicker({ format: 'mm/dd/yyyy' });
    if (order.customerContactDateReq) {
      this.ui.requiredCustomerContactDate.datepicker('setDate', formatDisplayDate(order.customerContactDateReq));
    }

    this.ui.actualCustomerContactDate.datepicker({ format: 'mm/dd/yyyy' });
    if (order.customerContactedOn) {
      this.ui.actualCustomerContactDate.datepicker('setDate', formatDisplayDate(order.customerContactedOn));
    }

    this.ui.epcSubmissionDate.datepicker({ format: 'mm/dd/yyyy' });
    if (order.submissionToEPCDateReq) {
      this.ui.epcSubmissionDate.datepicker('setDate', formatDisplayDate(order.submissionToEPCDateReq));
    }

    this.ui.orderPackageSubmittedOn.datepicker({ format: 'mm/dd/yyyy' });
    if (order.orderPackageSubmittedOn) {
      this.ui.orderPackageSubmittedOn.datepicker('setDate', formatDisplayDate(order.orderPackageSubmittedOn));
    }

    if (!this.model.get('editable')) {
      makeFormReadOnly(this.$el);
    }
    this.model.set('popChanged', false);
  },

  toggleForm(state) {
    this.ui.orderForm.find(':input').prop('disabled', !state);
  },

  async onAttach() {
    if (this.model.get('showValidation')) {
      const editable = this.model.get('editable');
      try {
        this.toggleForm(true);
        await validateForm(this.ui.orderForm);
      } catch {
        // Close datepicker if input is disabled
        if (!editable) {
          this.ui.dateInputs.datepicker('hide');
        }
      } finally {
        this.toggleForm(editable);
      }
    }
  },

  onChangePopDate() {
    this.model.set('popChanged', true);
  },

  showDateCalcError() {
    showErrorMessage({
      errorCode: 'dateCalcError',
      errorTitle: 'Error Constructing Timeline',
      errorMessage:
        'An error while constructing the timeline. Try again or contact an administrator if your issue persists.'
    });
  },

  showDateCalcPIINSPIINError() {
    showErrorMessage({
      errorCode: 'dateCalcPiinSpiinError',
      errorTitle: 'Invalid Terming Contract #',
      errorMessage: 'An invalid terming contract number was entered.'
    });
  },

  setCalculatedDates(dates) {
    this.ui.requiredCustomerContactDate.val(dates?.initialContactReqDate || '');
    this.ui.epcSubmissionDate.val(dates?.opSubmissionReqDate || '');
  },

  showUnauthorizedDateCalcError(message) {
    showErrorMessage({
      errorCode: 'dateCalcError',
      errorTitle: 'Unauthorized User',
      errorMessage: `${message}, unable to calculate dates.`
    });
  },

  async onClickDateCalc(e) {
    e.preventDefault();
    await validateForm(this.ui.popDate);
    await ValidateOrderModule.validate({ validateIfNotPristine: true });

    const spinner = Ladda.create(this.ui.btnDateCalc[0]);
    spinner.start();

    const popDate = this.ui.popDate.val();
    const piinSpiin = this.ui.piinSpiin.val();

    try {
      await this.onSave();
      const dates = await CalculateDatesForOrder(this.model.get('order').orderId, popDate, piinSpiin);
      this.setCalculatedDates(dates);
    } catch (err) {
      if (err?.message?.string === 'AWP_NOT_FOUND') {
        this.showDateCalcPIINSPIINError();
      } else if (err?.message?.string === 'Unauthorized') {
        this.showUnauthorizedDateCalcError(err.message.detail);
      } else {
        this.showDateCalcError();
      }
    } finally {
      spinner.stop();
    }
  },

  async onSave(e) {
    const orderStatus = this.model.get('order').statusId;
    if (orderStatus === '5' || orderStatus === '6' || orderStatus === '7') {
      const isValid = await ValidateOrderModule.validate();
      if (!isValid) return;
    }
    const formData = Syphon.serialize(this);
    formData.orderId = this.model.get('order').orderId;
    if (this.model.get('popChanged')) {
      formData.popChanged = 1;
    }
    try {
      await UpdateOrderInformation(formData);

      showSuccessMessage({
        code: 'saveSuccess',
        title: 'Save Success',
        message: 'Order Information Saved.',
        hideAfter: 1
      });
    } catch {
      throw new Error();
    }
  },

  showSubmissionError(orderId) {
    showErrorMessage({
      errorCode: 'submissionError',
      errorTitle: 'Submission Error',
      errorMessage: `There was an error submitting order ${orderId}. Please try again or contact an administrator if the issue persists.`,
      hideAfter: 5
    });
  },

  onHasErrors() {
    this.trigger('has:errors');
  },

  onNoErrors() {
    this.trigger('no:errors');
  },

  async validate({ calledOnReturn } = {}) {
    this.onNoErrors();

    // information fields are only relevant when an order is approved
    if (calledOnReturn) {
      return true;
    }

    try {
      this.toggleForm(true);
      return await validateForm(this.ui.orderForm);
    } catch {
      this.showSubmissionError(this.model.get('order')?.orderId);
      this.onHasErrors();
      return false;
    } finally {
      this.toggleForm(this.model.get('editable'));
    }
  }
});

export default OrderInformationView;
